import { useState, useEffect } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: "General",     label: "General",     emoji: "😄" },
  { id: "Dad",         label: "Dad Jokes",   emoji: "👨" },
  { id: "Programming", label: "Programming", emoji: "💻" },
  { id: "Dark",        label: "Dark",        emoji: "🌑" },
];

const CATEGORY_API_MAP = {
  General:     "Misc",
  Dad:         "Pun",
  Programming: "Programming",
  Dark:        "Dark",
};

const JOKES_PER_CATEGORY = 5;
const STORAGE_KEY = "healhub_jokes_cache";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getTodayString() {
  return new Date().toISOString().slice(0, 10); // "2026-03-05"
}

async function fetchJokesForCategory(category) {
  const apiCategory = CATEGORY_API_MAP[category];
  try {
    // JokeAPI throws a 400 error if you use safe-mode with the Dark category
    const safeModeParam = apiCategory === "Dark" ? "" : "&safe-mode";

    const res = await fetch(
      `https://v2.jokeapi.dev/joke/${apiCategory}?amount=${JOKES_PER_CATEGORY}&blacklistFlags=racist,sexist,explicit${safeModeParam}`
    );
    const data = await res.json();
    if (data.error) return getFallbackJokes(category);
    
    // API returns { jokes: [...] } for amount > 1
    const jokes = data.jokes || [data];
    return jokes.map((j) => ({
      type:     j.type,
      setup:    j.type === "twopart" ? j.setup    : null,
      delivery: j.type === "twopart" ? j.delivery : null,
      joke:     j.type === "single"  ? j.joke     : null,
    }));
  } catch {
    return getFallbackJokes(category);
  }
}

function getFallbackJokes(category) {
  const fallbacks = {
    General: [
      { type: "twopart", setup: "Why don't scientists trust atoms?", delivery: "Because they make up everything!" },
      { type: "single",  joke: "I told my wife she was drawing her eyebrows too high. She looked surprised." },
      { type: "twopart", setup: "Why did the scarecrow win an award?", delivery: "Because he was outstanding in his field!" },
      { type: "single",  joke: "I'm reading a book about anti-gravity. It's impossible to put down." },
      { type: "twopart", setup: "What do you call a fake noodle?", delivery: "An impasta!" },
    ],
    Dad: [
      { type: "twopart", setup: "I'm on a seafood diet.", delivery: "I see food and I eat it." },
      { type: "single",  joke: "I used to hate facial hair but then it grew on me." },
      { type: "twopart", setup: "Why can't you give Elsa a balloon?", delivery: "Because she'll let it go." },
      { type: "single",  joke: "I only know 25 letters of the alphabet. I don't know y." },
      { type: "twopart", setup: "What do you call cheese that isn't yours?", delivery: "Nacho cheese!" },
    ],
    Programming: [
      { type: "twopart", setup: "Why do programmers prefer dark mode?", delivery: "Because light attracts bugs!" },
      { type: "single",  joke: "A SQL query walks into a bar, walks up to two tables and asks... 'Can I join you?'" },
      { type: "twopart", setup: "How many programmers does it take to change a light bulb?", delivery: "None — that's a hardware problem." },
      { type: "single",  joke: "I would tell you a UDP joke, but you might not get it." },
      { type: "twopart", setup: "Why did the developer go broke?", delivery: "Because he used up all his cache." },
    ],
    Dark: [
      { type: "twopart", setup: "I have a joke about construction.", delivery: "I'm still working on it." },
      { type: "single",  joke: "My grief counsellor died. He was so good at his job I didn't care." },
      { type: "twopart", setup: "I asked my doctor how long I have to live.", delivery: "He said 'Ten.' Ten what? He just kept counting." },
      { type: "single",  joke: "I have many jokes about unemployed people — sadly, none of them work." },
      { type: "twopart", setup: "Why don't cannibals eat clowns?", delivery: "Because they taste funny." },
    ],
  };
  return fallbacks[category] || fallbacks.General;
}

async function fetchAllJokes() {
  const results = {};
  await Promise.all(
    CATEGORIES.map(async (cat) => {
      results[cat.id] = await fetchJokesForCategory(cat.id);
    })
  );
  return results;
}

function loadFromCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.date !== getTodayString()) return null;
    return parsed.jokes;
  } catch {
    return null;
  }
}

function saveToCache(jokes) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ date: getTodayString(), jokes }));
  } catch {}
}

// ─── Skeleton Loader ──────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div style={{
      borderRadius: 24,
      background: "var(--card-bg-secondary, #f5f3ff)",
      border: "1.5px solid var(--border-color, #e5e7eb)",
      padding: "36px 32px",
      minHeight: 240,
      display: "flex",
      flexDirection: "column",
      gap: 16,
      animation: "pulse 1.5s ease-in-out infinite",
    }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
      `}</style>
      <div style={{ height: 16, width: "40%", borderRadius: 8, background: "var(--border-color, #e5e7eb)" }} />
      <div style={{ height: 20, width: "85%", borderRadius: 8, background: "var(--border-color, #e5e7eb)" }} />
      <div style={{ height: 20, width: "70%", borderRadius: 8, background: "var(--border-color, #e5e7eb)" }} />
      <div style={{ height: 16, width: "55%", borderRadius: 8, background: "var(--border-color, #e5e7eb)", marginTop: 8 }} />
    </div>
  );
}

// ─── Single Joke Card ─────────────────────────────────────────────────────────
function JokeCard({ joke, category, index, total }) {
  const [revealed, setRevealed] = useState(false);
  const [reacted,  setReacted]  = useState(false);
  const cat = CATEGORIES.find((c) => c.id === category);

  // reset punchline reveal when joke changes
  useEffect(() => {
    setRevealed(false);
    setReacted(false);
  }, [joke]);

  const isTwoPart = joke.type === "twopart";

  return (
    <div style={{
      borderRadius: 24,
      background: "linear-gradient(135deg, var(--primary-light, #EDE9FE) 0%, var(--secondary-light, #FFF0F7) 100%)",
      border: "1.5px solid var(--border-subtle, #e5e7eb)",
      padding: "32px 28px 24px",
      position: "relative",
      overflow: "hidden",
      minHeight: 220,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}>
      {/* Big decorative quote */}
      <div style={{
        position: "absolute", top: 10, left: 20,
        fontSize: "6rem", lineHeight: 1,
        color: "var(--primary-color, #7C4DFF)",
        opacity: 0.07, fontFamily: "Georgia, serif",
        userSelect: "none", pointerEvents: "none",
      }}>"</div>

      {/* Category badge + joke counter */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "var(--primary-color, #7C4DFF)",
          color: "#fff", borderRadius: 99,
          padding: "4px 14px", fontSize: "0.72rem", fontWeight: 700,
          letterSpacing: "0.04em",
        }}>
          {cat?.emoji} {cat?.label}
        </span>
        <span style={{
          fontSize: "0.72rem", fontWeight: 700,
          color: "var(--text-muted, #9ca3af)",
          background: "var(--bg-secondary, #f9fafb)",
          borderRadius: 99, padding: "4px 12px",
          border: "1px solid var(--border-color, #e5e7eb)",
        }}>
          {index + 1} / {total}
        </span>
      </div>

      {/* Joke content */}
      <div style={{ flex: 1, paddingLeft: 4 }}>
        {isTwoPart ? (
          <>
            {/* Setup */}
            <p style={{
              fontFamily: "'DM Serif Display', Georgia, serif",
              fontSize: "clamp(1rem, 2.2vw, 1.2rem)",
              lineHeight: 1.65,
              color: "var(--text-color, #111)",
              margin: "0 0 18px",
              fontStyle: "italic",
            }}>
              {joke.setup}
            </p>

            {/* Punchline reveal */}
            {!revealed ? (
              <button
                onClick={() => setRevealed(true)}
                style={{
                  background: "var(--primary-color, #7C4DFF)",
                  color: "#fff", border: "none",
                  borderRadius: 12, padding: "8px 20px",
                  fontSize: "0.82rem", fontWeight: 700,
                  cursor: "pointer", letterSpacing: "0.03em",
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
              >
                Show Punchline 👇
              </button>
            ) : (
              <div style={{
                borderLeft: "3px solid var(--primary-color, #7C4DFF)",
                paddingLeft: 16,
                marginTop: 4,
              }}>
                <p style={{
                  fontFamily: "'DM Serif Display', Georgia, serif",
                  fontSize: "clamp(1rem, 2.2vw, 1.2rem)",
                  lineHeight: 1.65,
                  color: "var(--primary-color, #7C4DFF)",
                  margin: 0,
                  fontWeight: 700,
                }}>
                  {joke.delivery}
                </p>
              </div>
            )}
          </>
        ) : (
          // Single joke
          <p style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: "clamp(1rem, 2.2vw, 1.2rem)",
            lineHeight: 1.65,
            color: "var(--text-color, #111)",
            margin: 0,
            fontStyle: "italic",
          }}>
            {joke.joke}
          </p>
        )}
      </div>

      {/* React button */}
      <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={() => setReacted(r => !r)}
          style={{
            background: reacted ? "var(--accent-light, #fef3c7)" : "var(--bg-secondary, #f9fafb)",
            border: reacted ? "1.5px solid var(--accent-color, #f59e0b)" : "1.5px solid var(--border-color, #e5e7eb)",
            borderRadius: 99, padding: "6px 16px",
            fontSize: "0.82rem", fontWeight: 700,
            cursor: "pointer", transition: "all 0.2s",
            color: reacted ? "var(--accent-color, #f59e0b)" : "var(--text-muted, #9ca3af)",
          }}
        >
          {reacted ? "😂 Haha'd!" : "😂 Haha"}
        </button>
      </div>
    </div>
  );
}

// ─── Main JokesSection Component ──────────────────────────────────────────────
export default function JokesSection() {
  const [allJokes,   setAllJokes]   = useState(null);   // { General: [...], Dad: [...], ... }
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(false);
  const [category,   setCategory]   = useState("General");
  const [jokeIndex,  setJokeIndex]  = useState(0);

  // Reset joke index when category changes
  useEffect(() => { setJokeIndex(0); }, [category]);

  // Load jokes — cache first, then API
  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(false);
      try {
        const cached = loadFromCache();
        if (cached) {
          setAllJokes(cached);
        } else {
          const fresh = await fetchAllJokes();
          saveToCache(fresh);
          setAllJokes(fresh);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const currentJokes = allJokes?.[category] || [];
  const currentJoke  = currentJokes[jokeIndex];
  const total        = currentJokes.length;

  function prev() { setJokeIndex(i => (i - 1 + total) % total); }
  function next() { setJokeIndex(i => (i + 1) % total); }

  return (
    <div className="card animate-in" style={{ marginTop: 0 }}>
      <h2 style={{ marginBottom: 8 }}>😂 Daily Jokes</h2>
      <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginBottom: 24 }}>
        Fresh jokes every day. Pick a category and scroll through — new batch at midnight! 🌙
      </p>

      {/* Category pills */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setCategory(cat.id)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 18px", borderRadius: 99,
              border: category === cat.id
                ? "2px solid var(--primary-color, #7C4DFF)"
                : "1.5px solid var(--border-color, #e5e7eb)",
              background: category === cat.id
                ? "var(--primary-light, #EDE9FE)"
                : "var(--bg-secondary, #f9fafb)",
              color: category === cat.id
                ? "var(--primary-color, #7C4DFF)"
                : "var(--text-secondary-color, #6b7280)",
              fontWeight: category === cat.id ? 700 : 500,
              fontSize: "0.85rem", cursor: "pointer",
              transition: "all 0.2s",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {cat.emoji} {cat.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <SkeletonCard />
      ) : error ? (
        <div style={{
          textAlign: "center", padding: "40px 0",
          color: "var(--text-muted)", fontSize: "0.9rem",
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>😅</div>
          <p>Couldn't load jokes right now. Check your connection and try again.</p>
          <button
            className="btn secondary-btn"
            style={{ marginTop: 12 }}
            onClick={() => { localStorage.removeItem(STORAGE_KEY); window.location.reload(); }}
          >
            Try Again
          </button>
        </div>
      ) : currentJoke ? (
        <>
          <JokeCard
            joke={currentJoke}
            category={category}
            index={jokeIndex}
            total={total}
          />

          {/* Prev / Next navigation */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginTop: 20,
          }}>
            <button
              className="btn secondary-btn"
              onClick={prev}
              style={{ fontSize: "0.85rem", padding: "8px 20px" }}
            >
              ← Prev
            </button>

            {/* Dot indicators */}
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {currentJokes.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setJokeIndex(i)}
                  style={{
                    width: i === jokeIndex ? 20 : 8,
                    height: 8, borderRadius: 99,
                    background: i === jokeIndex
                      ? "var(--primary-color, #7C4DFF)"
                      : "var(--border-color, #e5e7eb)",
                    border: "none", cursor: "pointer",
                    transition: "all 0.25s ease",
                    padding: 0,
                  }}
                />
              ))}
            </div>

            <button
              className="btn secondary-btn"
              onClick={next}
              style={{ fontSize: "0.85rem", padding: "8px 20px" }}
            >
              Next →
            </button>
          </div>

          {/* Cache info */}
          <p style={{
            textAlign: "center", marginTop: 16,
            fontSize: "0.7rem", color: "var(--text-muted)",
          }}>
            📅 Today's jokes — refreshes at midnight
          </p>
        </>
      ) : null}
    </div>
  );
}